import React, { useState, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, RefreshCw, Calendar, FileDown, TrendingUp, TrendingDown, Minus, Edit2, Target, PiggyBank, ArrowRightLeft, X, Receipt, ArrowUpRight } from 'lucide-react';
import { ModalAgregarPresupuesto } from '../components/ModalAgregarPresupuesto';
import { exportarPresupuestosPDF } from '../utils/pdfUtils';
import { calcularResumenPresupuesto, calcularEstadoPresupuesto, limiteEnPeriodo, tieneExcepcionEnMes } from '../utils/presupuestoUtils';
import { Presupuesto, Traspaso, TraspasoRecurrente, Transaccion } from '../types';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';
import { useConfirmar } from '../context/ConfirmarContext';
import { useLimite } from '../hooks/useLimite';
import { ContadorLimite } from '../components/plus/ContadorLimite';

// ── Helpers de ritmo ──────────────────────────────────────────────────────────

function diasEnMes(anio: number, mes: number): number {
  return new Date(anio, mes + 1, 0).getDate();
}

function esBisiesto(anio: number): boolean {
  return (anio % 4 === 0 && anio % 100 !== 0) || anio % 400 === 0;
}

function diaDelAnio(fecha: Date): number {
  const inicio = new Date(fecha.getFullYear(), 0, 0);
  return Math.floor((fecha.getTime() - inicio.getTime()) / 86400000);
}

interface Ritmo {
  gastoDiario: number;     // límite / días del período
  diasTotales: number;     // días del período completo
  diasTranscurridos: number;
  ritmoEsperado: number;   // lo que "debería" llevar gastado hoy
  porcentajeRitmo: number; // ritmoEsperado / límite * 100 (para la línea en la barra)
}

function calcularRitmo(limite: number, periodicidad: 'mensual' | 'anual'): Ritmo {
  const hoy = new Date();
  let diasTotales: number;
  let diasTranscurridos: number;

  if (periodicidad === 'mensual') {
    diasTotales = diasEnMes(hoy.getFullYear(), hoy.getMonth());
    diasTranscurridos = hoy.getDate();
  } else {
    diasTotales = esBisiesto(hoy.getFullYear()) ? 366 : 365;
    diasTranscurridos = diaDelAnio(hoy);
  }

  const gastoDiario = limite / diasTotales;
  const ritmoEsperado = gastoDiario * diasTranscurridos;
  const porcentajeRitmo = Math.min((ritmoEsperado / limite) * 100, 100);

  return { gastoDiario, diasTotales, diasTranscurridos, ritmoEsperado, porcentajeRitmo };
}

// ── Componente ────────────────────────────────────────────────────────────────

// ── Tarjeta anual mejorada ────────────────────────────────────────────────────

// ── Modal de detalle de movimientos del presupuesto ──────────────────────────

interface DetalleMovimientosModalProps {
  presupuesto: Presupuesto;
  transacciones: Transaccion[];
  traspasos: (Traspaso | TraspasoRecurrente)[];
  esAhorro: boolean;
  periodoVista: string; // 'YYYY-MM' (mensual) o 'YYYY' (anual): período que se está viendo
  onClose: () => void;
}

const DetalleMovimientosModal: React.FC<DetalleMovimientosModalProps> = ({
  presupuesto, transacciones, traspasos, esAhorro, periodoVista, onClose
}) => {
  useCerrarConEscape(onClose);
  const { categorias, cuentas, inversiones } = useData();

  const esMensual = presupuesto.periodicidad === 'mensual';

  // Transacciones de gasto vinculadas a este presupuesto por id literal (modelo
  // evergreen: el id del documento es permanente). Se filtran por el período visto,
  // no por el mes de inicio del presupuesto.
  const txVinculadas = useMemo(() =>
    transacciones
      .filter(t => {
        if (t.tipo !== 'gasto') return false;
        if (t.traspasoId && !t.presupuestoId) return false;
        if (t.presupuestoId !== presupuesto.id) return false;
        const f = new Date(t.fecha);
        const fMes  = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
        const fAnio = String(f.getFullYear());
        return esMensual ? fMes === periodoVista : fAnio === periodoVista;
      })
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()),
  [transacciones, presupuesto.id, periodoVista, esMensual]);

  // Traspasos a inversión vinculados (solo Traspaso, no recurrentes — los recurrentes son plantillas)
  const traspasosVinculados = useMemo(() =>
    traspasos
      .filter((t): t is Traspaso => {
        if (!('presupuestoDestinoId' in t)) return false;
        if ((t as Traspaso).presupuestoDestinoId !== presupuesto.id) return false;
        if ('periodicidad' in t) return false;
        const f = new Date((t as Traspaso).fecha);
        const fMes  = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
        const fAnio = String(f.getFullYear());
        return esMensual ? fMes === periodoVista : fAnio === periodoVista;
      })
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()),
  [traspasos, presupuesto.id, periodoVista, esMensual]);

  const totalTx       = txVinculadas.reduce((s, t) => s + t.cantidad, 0);
  const totalTraspaso = traspasosVinculados.reduce((s, t) => s + t.cantidad, 0);

  const getNombreCategoria = (id: string) => {
    const c = categorias.find(c => c.id === id);
    return c ? `${c.icono} ${c.nombre}` : id;
  };
  const getNombreCuenta = (id?: string) => {
    if (!id) return '—';
    return cuentas.find(c => c.id === id)?.nombre ?? '—';
  };
  const getNombreInversion = (id?: string) => {
    if (!id) return '—';
    return inversiones.find(i => i.id === id)?.nombre ?? '—';
  };

  const hayMovimientos = txVinculadas.length > 0 || traspasosVinculados.length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center capa-modal z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-full flex flex-col"
        onClick={e => e.stopPropagation()}>

        {/* Cabecera */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{presupuesto.nombre}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {esAhorro ? 'Movimientos de ahorro acumulados' : 'Gastos imputados al presupuesto'}
              {' · '}{presupuesto.periodicidad === 'anual' ? 'año' : 'periodo'} {periodoVista}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Resumen rápido */}
        <div className="px-5 py-3 grid grid-cols-2 gap-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
          {esAhorro ? (
            <>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Gastos directos</p>
                <p className="font-bold text-red-600 dark:text-red-400 text-sm">€{totalTx.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Traspasos a inversión</p>
                <p className="font-bold text-blue-600 dark:text-blue-400 text-sm">€{totalTraspaso.toFixed(2)}</p>
              </div>
            </>
          ) : (
            <div className="col-span-2 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total gastado</p>
              <p className="font-bold text-gray-900 dark:text-white text-base">€{totalTx.toFixed(2)}</p>
            </div>
          )}
        </div>

        {/* Lista de movimientos */}
        <div className="overflow-y-auto flex-1 px-5 py-3 space-y-4">
          {!hayMovimientos && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
              No hay movimientos registrados para este presupuesto.
            </div>
          )}

          {/* Gastos */}
          {txVinculadas.length > 0 && (
            <div>
              {esAhorro && (
                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Receipt className="w-3 h-3" /> Gastos directos
                </p>
              )}
              <div className="space-y-1.5">
                {txVinculadas.map(t => (
                  <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {t.descripcion || getNombreCategoria(t.categoriaId)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(t.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {' · '}{getNombreCuenta(t.cuentaId)}
                      </p>
                    </div>
                    <span className="font-bold text-red-600 dark:text-red-400 text-sm shrink-0">
                      −€{t.cantidad.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Traspasos a inversión */}
          {traspasosVinculados.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3" /> Traspasos a inversión
              </p>
              <div className="space-y-1.5">
                {traspasosVinculados.map(t => (
                  <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {t.descripcion || getNombreInversion(t.inversionDestinoId)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(t.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {' · '}{getNombreInversion(t.inversionDestinoId)}
                      </p>
                    </div>
                    <span className="font-bold text-blue-600 dark:text-blue-400 text-sm shrink-0">
                      +€{t.cantidad.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Tarjeta mensual ───────────────────────────────────────────────────────────

interface TarjetaMensualProps {
  p: Presupuesto;
  traspasos: (Traspaso | TraspasoRecurrente)[];
  transacciones: Transaccion[];
  gasto: number; // gasto real ya calculado en el padre
  periodoVista: string; // 'YYYY-MM' del mes que se está viendo
  getNombreCategoria: (id: string) => string;
  onEditar: () => void;
  onEliminar: () => void;
}

const TarjetaMensual: React.FC<TarjetaMensualProps> = ({ p, traspasos, transacciones, gasto, periodoVista, getNombreCategoria, onEditar, onEliminar }) => {
  const [showDetalle, setShowDetalle] = useState(false);

  // ── Ritmo ajustado al MES QUE SE ESTÁ VIENDO (no al mes de inicio del
  // presupuesto). El filtro del padre garantiza que periodoVista >= p.mes. ──────
  const [mesAnio, mesMes] = periodoVista.split('-').map(Number);
  const hoy = new Date();
  const esMesActual = hoy.getFullYear() === mesAnio && hoy.getMonth() + 1 === mesMes;
  const esMesFuturo  = new Date(mesAnio, mesMes - 1, 1) > hoy;
  const diasTotales  = new Date(mesAnio, mesMes, 0).getDate();
  const diasTranscurridos = esMesFuturo ? 0 : esMesActual ? hoy.getDate() : diasTotales;

  // El importe de ESTE mes: puede ser distinto del general (solo este mes, o
  // un cambio que empezó más tarde)
  const limite = limiteEnPeriodo(p, periodoVista);
  const ajustadoEsteMes = tieneExcepcionEnMes(p, periodoVista);

  const gastoDiario    = limite / diasTotales;
  const ritmoEsperado  = gastoDiario * diasTranscurridos;
  const porcentajeRitmo = Math.min((ritmoEsperado / limite) * 100, 100);

  // Estado
  const resultado = calcularEstadoPresupuesto(
    gasto,
    ritmoEsperado,
    false, // mensuales son siempre control de gasto
    limite
  );
  const { emoji, label, diferencia, estado } = resultado;
  const porcentajeBarra = Math.min((gasto / limite) * 100, 100);
  const excedido = gasto > limite;

  const colorBarra =
    excedido ? 'bg-red-500'
    : estado === 'adelantado' ? 'bg-green-500'
    : estado === 'en_objetivo' ? 'bg-blue-500'
    : 'bg-red-500';

  const colorDiferencia =
    estado === 'adelantado' ? 'text-green-600 dark:text-green-400'
    : estado === 'en_objetivo' ? 'text-gray-600 dark:text-gray-400'
    : 'text-red-600 dark:text-red-400';

  const bgEstado =
    estado === 'adelantado' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    : estado === 'en_objetivo' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';

  const restante = Math.max(limite - gasto, 0);
  const diasRestantes = diasTotales - diasTranscurridos;
  const diarioPosible = diasRestantes > 0 ? restante / diasRestantes : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">

      {/* Encabezado */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white text-lg truncate">{p.nombre}</h3>
            {p.repetitivo && (
              <span className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                <RefreshCw className="w-3 h-3" /> Repetitivo
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{getNombreCategoria(p.categoriaId)}</p>
        </div>
        <div className="flex items-center gap-3 ml-2 shrink-0">
          <span className="text-lg" title={label}>{emoji}</span>
          <button onClick={onEditar} className="text-blue-600 hover:text-blue-800 transition" title="Editar">
            <Edit2 className="w-5 h-5" />
          </button>
          <button onClick={onEliminar} className="text-red-600 hover:text-red-800 transition" title="Eliminar">
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Métricas clave */}
      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">{ajustadoEsteMes ? 'Solo este mes' : 'Objetivo mensual'}</p>
          <p className="font-bold text-gray-900 dark:text-white text-sm mt-0.5">€{limite.toFixed(2)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">Ritmo diario</p>
          <p className="font-bold text-gray-900 dark:text-white text-sm mt-0.5">€{gastoDiario.toFixed(2)}</p>
        </div>
        <button
          onClick={() => setShowDetalle(true)}
          className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:ring-1 hover:ring-blue-400 transition group w-full">
          <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400">Gastado</p>
          <p className="font-bold text-gray-900 dark:text-white text-sm mt-0.5 group-hover:text-blue-600 dark:group-hover:text-blue-400">
            €{gasto.toFixed(2)}
          </p>
        </button>
      </div>

      {/* Barra de progreso con marcador de ritmo */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1.5">
          <span className="text-gray-600 dark:text-gray-400">
            <span className="font-bold text-gray-900 dark:text-white">€{gasto.toFixed(2)}</span>
            {' '}/ €{limite.toFixed(2)}
          </span>
          <span className={`font-semibold ${excedido ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
            {porcentajeBarra.toFixed(0)}%
          </span>
        </div>
        <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${colorBarra}`}
            style={{ width: `${porcentajeBarra}%` }}
          />
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-gray-500 dark:bg-gray-300 rounded"
            style={{ left: `${porcentajeRitmo}%` }}
            title={`Ritmo esperado: €${ritmoEsperado.toFixed(2)}`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>Día {diasTranscurridos} de {diasTotales}</span>
          <span>€{gastoDiario.toFixed(2)}/día</span>
        </div>
      </div>

      {/* Panel de estado */}
      <div className={`rounded-lg p-3 border ${bgEstado}`}>
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-gray-700 dark:text-gray-300">
            {emoji} {label}
          </span>
          <span className={`flex items-center gap-1 font-bold ${colorDiferencia}`}>
            {estado === 'en_objetivo'
              ? <><Minus className="w-3 h-3" /> En objetivo</>
              : diferencia > 0
                ? <><TrendingUp className="w-3 h-3" /> +€{Math.abs(diferencia).toFixed(2)}</>
                : <><TrendingDown className="w-3 h-3" /> -€{Math.abs(diferencia).toFixed(2)}</>
            }
          </span>
        </div>
        {!excedido && diasRestantes > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Restante: €{restante.toFixed(2)} · €{diarioPosible.toFixed(2)}/día durante {diasRestantes} días
          </p>
        )}
        {excedido && (
          <p className="text-xs text-red-600 dark:text-red-400 font-semibold mt-1.5">
            ¡Excedido en €{(gasto - limite).toFixed(2)}!
          </p>
        )}
      </div>

      {/* Modal de detalle */}
      {showDetalle && (
        <DetalleMovimientosModal
          presupuesto={p}
          transacciones={transacciones}
          traspasos={traspasos}
          esAhorro={false}
          periodoVista={periodoVista}
          onClose={() => setShowDetalle(false)}
        />
      )}
    </div>
  );
};

// ── Tarjeta anual mejorada ────────────────────────────────────────────────────

interface TarjetaAnualProps {
  p: Presupuesto;
  traspasos: (Traspaso | TraspasoRecurrente)[];
  transacciones: Transaccion[];
  gastoReal: number;
  mesReferencia: string; // 'YYYY-MM' del mes a mostrar en la barra mensual
  periodoVista: string;  // 'YYYY' del año que se está viendo
  getNombreCategoria: (id: string) => string;
  onEditar: () => void;
  onEliminar: () => void;
}

const TarjetaAnual: React.FC<TarjetaAnualProps> = ({ p, traspasos, transacciones, gastoReal, mesReferencia, periodoVista, getNombreCategoria, onEditar, onEliminar }) => {
  const [showDetalle, setShowDetalle] = useState(false);

  const presupuestoConGastoReal = { ...p, gastado: gastoReal };
  const { importeMensual, esperado, acumulado, resultado } = calcularResumenPresupuesto(presupuestoConGastoReal, traspasos, periodoVista);
  const { emoji, label, diferencia, porcentaje, estado } = resultado;
  const esAhorro = p.esObjetivoAhorro ?? false;

  // ── Barra mensual ──────────────────────────────────────────────────────────
  const [mesRefAnio, mesRefMes] = mesReferencia.split('-').map(Number);
  const hoy = new Date();
  const esMesActual = hoy.getFullYear() === mesRefAnio && hoy.getMonth() + 1 === mesRefMes;
  const diasTotalesMes = new Date(mesRefAnio, mesRefMes, 0).getDate();
  const diasTranscurridosMes = esMesActual ? hoy.getDate() : diasTotalesMes;
  const gastoDiarioMes = importeMensual / diasTotalesMes;
  const ritmoEsperadoMes = gastoDiarioMes * diasTranscurridosMes;
  const porcentajeRitmoMes = Math.min((ritmoEsperadoMes / importeMensual) * 100, 100);

  // Gasto real del mes de referencia vinculado a este presupuesto
  const gastoMes = useMemo(() => {
    const txMes = transacciones
      .filter(t =>
        t.presupuestoId === p.id &&
        t.tipo === 'gasto' &&
        !t.traspasoId &&
        new Date(t.fecha).toISOString().slice(0, 7) === mesReferencia
      )
      .reduce((s, t) => s + t.cantidad, 0);
    const tMes = (traspasos as Traspaso[])
      .filter(t =>
        (t as Traspaso).presupuestoDestinoId === p.id &&
        !('periodicidad' in t) &&
        new Date((t as Traspaso).fecha).toISOString().slice(0, 7) === mesReferencia
      )
      .reduce((s, t) => s + t.cantidad, 0);
    return txMes + tMes;
  }, [transacciones, traspasos, p.id, mesReferencia]);

  const pctMes = importeMensual > 0 ? Math.min((gastoMes / importeMensual) * 100, 150) : 0;
  const excedidoMes = gastoMes > importeMensual;
  const colorBarraMes = excedidoMes ? 'bg-red-500' : gastoMes > ritmoEsperadoMes * 1.1 ? 'bg-red-500' : gastoMes < ritmoEsperadoMes * 0.9 ? 'bg-green-500' : 'bg-blue-500';

  const labelMesRef = new Date(mesRefAnio, mesRefMes - 1).toLocaleString('es', { month: 'long', year: 'numeric' });
  const labelMesCapital = labelMesRef.charAt(0).toUpperCase() + labelMesRef.slice(1);

  // Porcentaje de barra respecto al esperado (no al límite anual)
  const pctBarra = esperado > 0 ? Math.min((acumulado / esperado) * 100, 150) : 0;
  const pctEsperadoSobreLimite = Math.min((esperado / p.limite) * 100, 100);

  const colorBarra =
    estado === 'adelantado' ? 'bg-green-500'
    : estado === 'en_objetivo' ? 'bg-blue-500'
    : 'bg-red-500';

  const colorDiferencia =
    estado === 'adelantado' ? 'text-green-600 dark:text-green-400'
    : estado === 'en_objetivo' ? 'text-gray-600 dark:text-gray-400'
    : 'text-red-600 dark:text-red-400';

  const bgEstado =
    estado === 'adelantado' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
    : estado === 'en_objetivo' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';

  const restanteAnual = Math.max(p.limite - acumulado, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">

      {/* Encabezado */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white text-lg truncate">{p.nombre}</h3>
            {/* Badge tipo */}
            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${
              esAhorro
                ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300'
                : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'
            }`}>
              {esAhorro ? <><PiggyBank className="w-3 h-3" /> Ahorro</> : <><Target className="w-3 h-3" /> Gasto</>}
            </span>
            {p.repetitivo && (
              <span className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                <RefreshCw className="w-3 h-3" /> Repetitivo
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{getNombreCategoria(p.categoriaId)}</p>
        </div>

        {/* Indicador de estado + acciones */}
        <div className="flex items-center gap-3 ml-2 shrink-0">
          <span className="text-lg" title={label}>{emoji}</span>
          <button onClick={onEditar} className="text-blue-600 hover:text-blue-800 transition" title="Editar">
            <Edit2 className="w-5 h-5" />
          </button>
          <button onClick={onEliminar} className="text-red-600 hover:text-red-800 transition" title="Eliminar">
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Métricas clave */}
      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">Objetivo anual</p>
          <p className="font-bold text-gray-900 dark:text-white text-sm mt-0.5">€{p.limite.toFixed(2)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">Mensual</p>
          <p className="font-bold text-gray-900 dark:text-white text-sm mt-0.5">€{importeMensual.toFixed(2)}</p>
        </div>
        <button
          onClick={() => setShowDetalle(true)}
          className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 text-center hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:ring-1 hover:ring-blue-400 transition group w-full">
          <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400">
            {esAhorro ? 'Acumulado' : 'Gastado'}
          </p>
          <p className="font-bold text-gray-900 dark:text-white text-sm mt-0.5 group-hover:text-blue-600 dark:group-hover:text-blue-400">
            €{acumulado.toFixed(2)}
          </p>
        </button>
      </div>

      {/* Barra de progreso referenciada al esperado */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1.5">
          <span className="text-gray-600 dark:text-gray-400">
            Progreso vs objetivo
          </span>
          <span className="font-semibold text-gray-700 dark:text-gray-300">{porcentaje.toFixed(0)}%</span>
        </div>
        <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          {/* Barra de acumulado */}
          <div
            className={`h-3 rounded-full transition-all ${colorBarra}`}
            style={{ width: `${Math.min(pctBarra, 100)}%` }}
          />
          {/* Línea del esperado a hoy sobre el límite anual */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-gray-500 dark:bg-gray-300 rounded"
            style={{ left: `${pctEsperadoSobreLimite}%` }}
            title={`Esperado a hoy: €${esperado.toFixed(2)}`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>Esperado a hoy: €{esperado.toFixed(2)}</span>
          <span>Restante: €{restanteAnual.toFixed(2)}</span>
        </div>
      </div>

      {/* Barra de progreso mensual */}
      {estado !== 'no_iniciado' && (
        <div className="mb-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          <div className="flex justify-between text-sm mb-1.5">
            <span className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wide">
              Mes actual · {labelMesCapital}
            </span>
            <span className={`font-semibold text-xs ${excedidoMes ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
              {pctMes.toFixed(0)}%
            </span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-700 dark:text-gray-300">
              <span className="font-bold">€{gastoMes.toFixed(2)}</span>
              {' '}/ €{importeMensual.toFixed(2)}
            </span>
          </div>
          <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${colorBarraMes}`}
              style={{ width: `${Math.min(pctMes, 100)}%` }}
            />
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-gray-500 dark:bg-gray-300 rounded"
              style={{ left: `${porcentajeRitmoMes}%` }}
              title={`Ritmo esperado mes: €${ritmoEsperadoMes.toFixed(2)}`}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>Esperado {diasTranscurridosMes} / {diasTotalesMes}</span>
            <span>€{gastoDiarioMes.toFixed(2)}/día</span>
          </div>
        </div>
      )}

      {/* Panel de estado */}
      <div className={`rounded-lg p-3 border ${bgEstado}`}>
        {estado === 'no_iniciado' ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            🟡 El ejercicio {periodoVista} aún no ha comenzado
          </p>
        ) : (
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {emoji} {label}
            </span>
            <span className={`flex items-center gap-1 font-bold ${colorDiferencia}`}>
              {estado === 'en_objetivo'
                ? <><Minus className="w-3 h-3" /> En objetivo</>
                : diferencia > 0
                  ? <><TrendingUp className="w-3 h-3" /> +€{Math.abs(diferencia).toFixed(2)}</>
                  : <><TrendingDown className="w-3 h-3" /> -€{Math.abs(diferencia).toFixed(2)}</>
              }
            </span>
          </div>
        )}
        {p.mesInicio && p.anioInicio && estado !== 'no_iniciado' && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Inicio: {new Date(p.anioInicio, p.mesInicio - 1).toLocaleString('es', { month: 'long', year: 'numeric' })}
            {' '}· Incluye consumo teórico de meses previos
          </p>
        )}
      </div>

      {/* ── Modal de detalle de movimientos ── */}
      {showDetalle && <DetalleMovimientosModal
        presupuesto={p}
        transacciones={transacciones}
        traspasos={traspasos}
        esAhorro={esAhorro}
        periodoVista={periodoVista}
        onClose={() => setShowDetalle(false)}
      />}
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────

export const Presupuestos: React.FC = () => {
  const confirmar = useConfirmar();
  const { presupuestos, categorias, transacciones, traspasos, traspasosRecurrentes, eliminarPresupuesto, loading } = useData();
  const navigate = useNavigate();
  const limite = useLimite('presupuestos');
  const [showModal, setShowModal] = useState(false);
  const [presupuestoEditar, setPresupuestoEditar] = useState<typeof presupuestos[0] | undefined>(undefined);
  const [mesActual]  = useState(new Date().toISOString().slice(0, 7));
  const [anioActual] = useState(new Date().getFullYear().toString());
  const [vistaActiva, setVistaActiva] = useState<'mensual' | 'anual'>('mensual');
  // Mes seleccionado en la vista mensual — por defecto el mes actual
  const [mesVista, setMesVista] = useState(new Date().toISOString().slice(0, 7));
  // Año seleccionado en la vista anual — por defecto el año actual
  const [anioVista, setAnioVista] = useState(new Date().getFullYear().toString());

  // Meses navegables (selector mensual): rango LIBRE, no derivado de qué documentos
  // existan. Modelo evergreen: hay un único documento por presupuesto y el mes que se
  // ve solo filtra transacciones. Rango = desde el inicio (inception) más antiguo de
  // los presupuestos mensuales (o 24 meses atrás como fallback) hasta 12 meses futuros.
  const mesesDisponibles = useMemo(() => {
    const hoy = new Date();
    const fallbackInicio = new Date(hoy.getFullYear(), hoy.getMonth() - 24, 1);
    let inicioStr = `${fallbackInicio.getFullYear()}-${String(fallbackInicio.getMonth() + 1).padStart(2, '0')}`;
    presupuestos
      .filter(p => p.periodicidad === 'mensual')
      .forEach(p => { if (p.mes < inicioStr) inicioStr = p.mes; });
    const finDate = new Date(hoy.getFullYear(), hoy.getMonth() + 12, 1);
    const [iAnio, iMes] = inicioStr.split('-').map(Number);
    const meses: string[] = [];
    let cursor = new Date(iAnio, iMes - 1, 1);
    while (cursor <= finDate) {
      meses.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    if (!meses.includes(mesActual)) { meses.push(mesActual); meses.sort(); }
    return meses;
  }, [presupuestos, mesActual]);

  // Años navegables (selector anual): rango libre, mismo criterio que los meses.
  const aniosDisponibles = useMemo(() => {
    const anioHoy = parseInt(anioActual, 10);
    let inicio = anioHoy - 3;
    presupuestos
      .filter(p => p.periodicidad === 'anual')
      .forEach(p => { const a = parseInt(p.mes, 10); if (a < inicio) inicio = a; });
    const fin = anioHoy + 2;
    const anios: string[] = [];
    for (let a = inicio; a <= fin; a++) anios.push(String(a));
    return anios;
  }, [presupuestos, anioActual]);

  const presupuestosActivos = useMemo(() => {
    const periodo = vistaActiva === 'mensual' ? mesVista : anioVista;
    return presupuestos.filter(p => {
      if (p.periodicidad !== vistaActiva) return false;
      // Repetitivo: activo desde su inception (p.mes) en adelante.
      // Puntual (no repetitivo): solo en su período exacto.
      return p.repetitivo ? p.mes <= periodo : p.mes === periodo;
    });
  }, [presupuestos, vistaActiva, mesVista, anioVista]);

  /**
   * Gasto real de un presupuesto para el período de la vista (mesVista/anioVista).
   * Empareja las transacciones por id literal (modelo evergreen: el id del documento
   * es permanente) y las filtra por fecha dentro del período que se está viendo.
   */
  const obtenerGasto = (p: Presupuesto) => {
    const periodo = p.periodicidad === 'mensual' ? mesVista : anioVista;
    return transacciones
      .filter(t => {
        if (t.tipo !== 'gasto') return false;
        if (t.traspasoId && !t.presupuestoId) return false;
        if (t.presupuestoId !== p.id) return false;
        const f = new Date(t.fecha);
        const fMes  = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
        const fAnio = String(f.getFullYear());
        return p.periodicidad === 'mensual' ? fMes === periodo : fAnio === periodo;
      })
      .reduce((s, t) => s + t.cantidad, 0);
  };

  const getNombreCategoria = (id: string) => {
    const c = categorias.find(c => c.id === id);
    return c ? `${c.icono} ${c.nombre}` : 'Desconocida';
  };

  const handleEliminar = async (id: string) => {
    if (await confirmar({ titulo: '¿Eliminar este presupuesto?', accion: 'Eliminar' })) {
      try { await eliminarPresupuesto(id); } catch (e) { console.error(e); }
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-gray-600 dark:text-gray-400">Cargando presupuestos...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">

        {/* Cabecera */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Presupuestos</h1>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => exportarPresupuestosPDF(presupuestos, categorias, transacciones,
                vistaActiva === 'mensual' ? mesVista : anioVista, vistaActiva)}
              className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold py-2 px-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition text-sm">
              <FileDown className="w-4 h-4" /> PDF
            </button>
            <button
              onClick={() => navigate('/finanzas/migracion-presupuestos')}
              className="flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-semibold py-2 px-3 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition text-sm">
              <ArrowRightLeft className="w-4 h-4" /> Migración
            </button>
            <button onClick={() => limite.intentar(() => { setPresupuestoEditar(undefined); setShowModal(true); })}
              className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition">
              <Plus className="w-5 h-5" /> Nuevo Presupuesto <ContadorLimite texto={limite.texto} />
            </button>
          </div>
        </div>

        {/* Tabs mensual/anual */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-8">
          <div className="flex flex-wrap gap-3 items-center">
            {(['mensual', 'anual'] as const).map(v => (
              <button key={v} onClick={() => setVistaActiva(v)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
                  vistaActiva === v
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}>
                <Calendar className="w-4 h-4" />
                {v === 'mensual' ? 'Mensual' : 'Anual'}
              </button>
            ))}

            {/* Selector de mes para vista mensual */}
            {vistaActiva === 'mensual' && (
              <div className="flex items-center gap-1 ml-1">
                <button
                  onClick={() => {
                    const idx = mesesDisponibles.indexOf(mesVista);
                    if (idx > 0) setMesVista(mesesDisponibles[idx - 1]);
                  }}
                  disabled={mesesDisponibles.indexOf(mesVista) === 0}
                  className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 transition">
                  ‹
                </button>
                <select
                  value={mesVista}
                  onChange={e => setMesVista(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {mesesDisponibles.map(m => {
                    const [y, mo] = m.split('-');
                    const label = new Date(parseInt(y), parseInt(mo) - 1).toLocaleString('es', { month: 'long', year: 'numeric' });
                    return <option key={m} value={m}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>;
                  })}
                </select>
                <button
                  onClick={() => {
                    const idx = mesesDisponibles.indexOf(mesVista);
                    if (idx < mesesDisponibles.length - 1) setMesVista(mesesDisponibles[idx + 1]);
                  }}
                  disabled={mesesDisponibles.indexOf(mesVista) === mesesDisponibles.length - 1}
                  className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 transition">
                  ›
                </button>
              </div>
            )}

            {/* Selector de año para vista anual */}
            {vistaActiva === 'anual' && (
              <div className="flex items-center gap-1 ml-1">
                <button
                  onClick={() => {
                    const idx = aniosDisponibles.indexOf(anioVista);
                    if (idx > 0) setAnioVista(aniosDisponibles[idx - 1]);
                  }}
                  disabled={aniosDisponibles.indexOf(anioVista) === 0}
                  className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 transition">
                  ‹
                </button>
                <select
                  value={anioVista}
                  onChange={e => setAnioVista(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {aniosDisponibles.map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    const idx = aniosDisponibles.indexOf(anioVista);
                    if (idx < aniosDisponibles.length - 1) setAnioVista(aniosDisponibles[idx + 1]);
                  }}
                  disabled={aniosDisponibles.indexOf(anioVista) === aniosDisponibles.length - 1}
                  className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-30 transition">
                  ›
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Resumen total — solo cuando el período es el actual ── */}
        {presupuestosActivos.length > 0 &&
         (vistaActiva === 'mensual' || anioVista === anioActual) && (() => {
          const limiteVista    = (p: Presupuesto) => limiteEnPeriodo(p, vistaActiva === 'mensual' ? mesVista : anioVista);
          const totalLimite    = presupuestosActivos.reduce((s, p) => s + limiteVista(p), 0);
          const totalGasto     = presupuestosActivos.reduce((s, p) => s + obtenerGasto(p), 0);
          const totalEsperado  = presupuestosActivos.reduce((s, p) => s + calcularRitmo(limiteVista(p), p.periodicidad).ritmoEsperado, 0);
          const totalDiario    = presupuestosActivos.reduce((s, p) => s + calcularRitmo(limiteVista(p), p.periodicidad).gastoDiario, 0);
          const diferencia     = totalGasto - totalEsperado;
          const adelantado     = diferencia > 0.01;
          const enRitmo        = Math.abs(diferencia) < 0.01;
          const porcentajeTotal = Math.min((totalGasto / totalLimite) * 100, 100);
          const { diasTranscurridos, diasTotales } = calcularRitmo(totalLimite, vistaActiva);

          return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 mb-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-900 dark:text-white text-base">
                  Resumen total — día {diasTranscurridos} de {diasTotales}
                </h2>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full font-semibold">
                  €{totalDiario.toFixed(2)}/día
                </span>
              </div>

              {/* Barra total con línea de ritmo */}
              <div className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400">
                    <span className="font-bold text-gray-900 dark:text-white">€{totalGasto.toFixed(2)}</span>
                    {' '}gastado de <span className="font-semibold">€{totalLimite.toFixed(2)}</span>
                  </span>
                  <span className="font-semibold text-gray-700 dark:text-gray-300">{porcentajeTotal.toFixed(0)}%</span>
                </div>
                <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${totalGasto > totalLimite ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ width: `${porcentajeTotal}%` }}
                  />
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-gray-500 dark:bg-gray-300 rounded"
                    style={{ left: `${Math.min((totalEsperado / totalLimite) * 100, 100)}%` }}
                    title={`Ritmo esperado: €${totalEsperado.toFixed(2)}`}
                  />
                </div>
              </div>

              {/* Métricas en fila */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Esperado hoy</p>
                  <p className="font-bold text-gray-900 dark:text-white text-sm mt-0.5">€{totalEsperado.toFixed(2)}</p>
                </div>
                <div className={`rounded-lg p-2 ${
                  enRitmo   ? 'bg-gray-50 dark:bg-gray-700/50'
                  : adelantado ? 'bg-red-50 dark:bg-red-900/20'
                  : 'bg-green-50 dark:bg-green-900/20'
                }`}>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Diferencia</p>
                  <p className={`font-bold text-sm mt-0.5 flex items-center justify-center gap-0.5 ${
                    enRitmo ? 'text-gray-600 dark:text-gray-400'
                    : adelantado ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                  }`}>
                    {enRitmo
                      ? <><Minus className="w-3 h-3" /> €0,00</>
                      : adelantado
                        ? <><TrendingUp className="w-3 h-3" /> +€{diferencia.toFixed(2)}</>
                        : <><TrendingDown className="w-3 h-3" /> -€{Math.abs(diferencia).toFixed(2)}</>
                    }
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Restante</p>
                  <p className="font-bold text-blue-600 dark:text-blue-400 text-sm mt-0.5">€{Math.max(totalLimite - totalGasto, 0).toFixed(2)}</p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Grid de presupuestos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {presupuestosActivos.length === 0 ? (
            <div className="col-span-full bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-600 dark:text-gray-400">
              No hay presupuestos {vistaActiva === 'mensual' ? 'para este mes' : 'para este año'}. ¡Crea uno!
            </div>
          ) : (
            presupuestosActivos.map(p => {
              // ── Tarjeta anual mejorada ──
              if (p.periodicidad === 'anual') {
                const todosTraspasos = [...traspasos, ...traspasosRecurrentes];
                const gastoReal = obtenerGasto(p);
                // Mes de referencia para la barra mensual, según el AÑO QUE SE ESTÁ
                // VIENDO: mes actual si es el año en curso, diciembre si es pasado,
                // enero si es futuro.
                const anioP = parseInt(anioVista, 10);
                const anioHoy = new Date().getFullYear();
                const mesRefParaBarra =
                  anioP < anioHoy ? `${anioVista}-12`
                  : anioP > anioHoy ? `${anioVista}-01`
                  : new Date().toISOString().slice(0, 7);
                return (
                  <TarjetaAnual
                    key={p.id}
                    p={p}
                    traspasos={todosTraspasos}
                    transacciones={transacciones}
                    gastoReal={gastoReal}
                    mesReferencia={mesRefParaBarra}
                    periodoVista={anioVista}
                    getNombreCategoria={getNombreCategoria}
                    onEditar={() => { setPresupuestoEditar(p); setShowModal(true); }}
                    onEliminar={() => handleEliminar(p.id)}
                  />
                );
              }

              // ── Tarjeta mensual ──
              const gasto = obtenerGasto(p);
              const todosTraspasos = [...traspasos, ...traspasosRecurrentes];
              return (
                <TarjetaMensual
                  key={p.id}
                  p={p}
                  traspasos={todosTraspasos}
                  transacciones={transacciones}
                  gasto={gasto}
                  periodoVista={mesVista}
                  getNombreCategoria={getNombreCategoria}
                  onEditar={() => { setPresupuestoEditar(p); setShowModal(true); }}
                  onEliminar={() => handleEliminar(p.id)}
                />
              );
            })
          )}
        </div>
      </div>

      {showModal && (
        <ModalAgregarPresupuesto
          onClose={() => { setShowModal(false); setPresupuestoEditar(undefined); }}
          presupuestoEditar={presupuestoEditar}
          periodoVista={mesVista}
        />
      )}
    </div>
  );
};
